import type { RequestLogin, ResponseAuth as ApiResponseAuth} from "./auth";
import {
    NUser,
    NOrganisation,
    NRole,
    NPermission,
    AuthNormalisedEntities,
} from "./auth";
import { ReportsNormalisedEntities } from "./reports";
import { NChart, DChart, ChartsNormalisedEntities } from "./charts";
import {
    NDeliveryBatch,
    NDeliveryType,
    NDeliveryStatusType,
    NDeliveryStatus,
    NDelivery,
    DeliveryNormalisedEntities,
} from "./deliveries";
import type { Album, Track } from "./music";
import {
    NAlbum,
    NTrack,
    NArtist,
    NGenre,
    NLabel,
    NAlbumFormat,
    NAlbumStatus,
    NAlbumTrack,
    NArtistPivot,
    MusicNormalisedEntities,
} from "./music";
import {
    NPlaylistSnapshotTrack,
    NTrack as NVendorTrack,
    NPlaylistSnapshot,
    NPlaylist,
    NPlaylistTrends,
    VendorNormalisedEntities,
    NPlaylistTrack as NVendorPlaylistTrack,
} from "./vendor";
import { FilesNormalisedEntities } from "./files";
import { NCountry, Region, LocalisationNormalisedEntities } from "./localisation";
import { NDsp, DspsNormalisedEntities } from "./dsps";
import { NBundle, NBundleStatus, UploadNormalisedEntities } from "./upload";
import type { ApiModel } from "./model";

type ApiResponseData = ApiResponseAuth | ApiModelCollection | ApiModel | ApiId |
    ApiSales | ApiDemographics | Album | ApiSearch | ApiUrl;
type ApiRequest = RequestLogin | {};

interface ApiResponse<D = ApiResponseData> {
    apiVersion: string;
    // context?: string;
    // id?: string;
    method?: string;
    // params?: any;
    data?: D;
    error?: ApiResponseError;
}

interface ApiResponseError {
    code?: number;
    message?: string;
    errors?: ApiResponseIndividualError[];
}

interface ApiResponseIndividualError {
    domain?: string;
    reason?: string;
    message?: string;
    location?: string;
    locationType?: string;
    // extendedHelp?: string;
    // sendReport?: string;
}

interface ApiModelCollection {
    kind: string;
    items: ApiModel[];
}

interface ApiModelCollectionPaginated extends ApiModelCollection {
    currentItemCount: number;
    itemsPerPage: number;
    pageIndex: number;
    totalItems?: number;
    totalPages?: number;
    nextLink: string;
    previousLink: string;
    pagingLinkTemplate: string;
    models?: {
        tracks?: Track[];
    };
}

interface ApiModelTrendsCollectionPaginated extends ApiModelCollectionPaginated {
    albumId: number;
    trackId: number;
    artistId: number;
}

interface ApiId {
    id: number;
}

interface ApiUrl {
    url: string;
}

interface ApiSales {
    albumId?: number;
    trackId?: number;
    artistId?: number;
    countryCode?: string;
    groupBy: string;
    sales: Array<{
        type: string;
        label?: string;
        data?: {
            [date: string]: number;
        };
        count?: number;
    }>;
    tracks?: ApiSales[];
    albums?: ApiSales[];
    countries?: ApiSales[];
}

interface ApiDemographics {
    album_id?: number;
    track_id?: number;
    artist_id?: number;
    age: Array<{
        vendor: string;
        data: {
            [age: string]: number;
        };
    }>;
    gender: Array<{
        vendor: string;
        data: {
            [gender: string]: number;
        };
    }>;
    region: Array<{
        vendor: string;
        data: {
            [region: string]: number;
        };
    }>;
    models: {
        regions: Region[];
    };
}

interface ApiHeadlineStats {
    kind: "headline-stats";
    album_id?: number;
    track_id?: number;
    artist_id?: number;
    headings: string[];
    all: number[];
    vendors: {
        [vendor: string]: number[];
    };
    countries: {
        [country: string]: number[];
    };
    countries_vendors: {
        [country: string]: {
            [vendor: string]: number[];
        };
    };
}

interface ApiSearch {
    albums: Album[];
    tracks: Track[];
}

type NormalisedEntities = AuthNormalisedEntities & MusicNormalisedEntities &
    ChartsNormalisedEntities & FilesNormalisedEntities & LocalisationNormalisedEntities &
    DeliveryNormalisedEntities & DspsNormalisedEntities & ReportsNormalisedEntities &
    UploadNormalisedEntities & VendorNormalisedEntities;

// Functions to determine the type of resposne.
function isApiModel(response: ApiResponseData): response is ApiModel {
    return (response as ApiModel).kind !== undefined &&
        (response as ApiModel).id !== undefined;
}

function isApiModelCollection(response: ApiResponseData): response is ApiModelCollection {
    return (response as ApiModelCollection).kind !== undefined &&
        (response as ApiModelCollection).items !== undefined;
}

function isApiModelCollectionPaginated(response: ApiResponseData): response is ApiModelCollectionPaginated {
    return (response as ApiModelCollection).kind !== undefined &&
        (response as ApiModelCollection).items !== undefined &&
        (response as ApiModelCollectionPaginated).currentItemCount !== undefined;
}

function isApiModelTrendsCollectionPaginated(
    response: ApiResponseData,
): response is ApiModelTrendsCollectionPaginated {
        return (response as ApiModelCollection).kind !== undefined &&
            (response as ApiModelCollection).items !== undefined &&
            (response as ApiModelCollectionPaginated).currentItemCount !== undefined &&
            (response as ApiModelCollection).kind === "playlist-trends";
    }

function isApiId(response: ApiResponseData): response is ApiId {
    return (response as ApiModel).kind === undefined &&
        (response as ApiModel).id !== undefined;
}

function isApiResponseAuth(response: ApiResponseData): response is ApiResponseAuth {
    return (response as ApiResponseAuth).access_token !== undefined;
}

function isApiSales(response: ApiResponseData): response is ApiSales {
    return (response as ApiSales).sales !== undefined;
}

function isApiDemographics(response: ApiResponseData): response is ApiDemographics {
    return (response as ApiDemographics).gender !== undefined;
}

function isApiHeadlineStats(response: ApiResponseData): response is ApiHeadlineStats {
    return (response as ApiHeadlineStats).kind === "headline-stats";
}

function isApiSearch(response: ApiResponseData): response is ApiSearch {
    return (response as ApiSearch).albums !== undefined;
}

function isApiUrl(response: ApiResponseData): response is ApiUrl {
    return (response as ApiModel).kind === undefined &&
        (response as ApiUrl).url !== undefined;
}

export type { ApiModel } from "./model";
export type {
    RequestLogin,
    ResponseAuth as ApiResponseAuth,
    User,
} from "./auth";
export type {
    Chart,
} from "./charts";
export type {
    Delivery,
    Batch as DeliveryBatch,
    Type as DeliveryType,
    Status as DeliveryStatus,
    StatusType as DeliveryStatusType,
} from "./deliveries";
export type {
    Dsp,
} from "./dsps";
export type {
    Image,
    File,
} from "./files";
export type {
    Country,
    Region,
} from "./localisation";
export type {
    Album,
    Track,
    Artist,
    Genre,
    Label,
    AlbumFormat,
    AlbumStatus,
    AlbumTrack,
    ArtistPivot,
} from "./music";
export type {
    Report,
} from "./reports";
export type {
    PlaylistTrends,
    Playlist,
    PlaylistSnapshot,
    Track as VendorTrack,
    PlaylistSnapshotTrack,
    PlaylistTrack as VendorPlaylistTrack,
} from "./vendor";
export {
    // Requests
    ApiRequest,

    // Responses
    ApiResponse,
    ApiResponseError,
    ApiResponseIndividualError,
    ApiModelCollection,
    ApiSales,
    ApiDemographics,
    ApiHeadlineStats,
    isApiSearch,

    // Models
    NCountry,
    NDsp,
    NChart,
    DChart,
    NUser,
    NOrganisation,
    NRole,
    NPermission,
    NAlbum,
    NTrack,
    NArtist,
    NGenre,
    NLabel,
    NAlbumFormat,
    NAlbumStatus,
    NAlbumTrack,
    NArtistPivot,
    NDeliveryBatch,
    NDeliveryType,
    NDeliveryStatusType,
    NDeliveryStatus,
    NDelivery,
    NBundle,
    NBundleStatus,
    NPlaylistTrends,
    NPlaylist,
    NPlaylistSnapshot,
    NVendorTrack,
    NPlaylistSnapshotTrack,
    NVendorPlaylistTrack,
    NormalisedEntities,

    // Functions
    isApiModel,
    isApiModelCollection,
    isApiModelCollectionPaginated,
    isApiModelTrendsCollectionPaginated,
    isApiId,
    isApiResponseAuth,
    isApiSales,
    isApiDemographics,
    isApiHeadlineStats,
    isApiUrl,
};
